import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from "recharts";
import { useI18n } from "../lib/i18n";

const COLORS = { sep: "#475569", cog: "#d97706", fun: "#e11d48" };
const LEVEL_CLS = { faible: "text-emerald-700", modéré: "text-amber-700", élevé: "text-rose-700" };

export const GriefResult = ({ scoring, subscales, levels, title }) => {
  const { t } = useI18n();
  if (!scoring) return null;
  const LEVEL_KEY = { faible: "low", modéré: "moderate", élevé: "high" };
  return (
    <Card data-testid="grief-result">
      <CardHeader className="pb-2"><CardTitle className="text-base">{title || t("griefScale.yourResult", "Votre résultat")}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium">{t("griefScale.total", "Score total")}</span>
          <span className="text-2xl font-semibold" data-testid="grief-total">{scoring.total} <span className="text-sm text-muted-foreground font-normal">/ {scoring.max_total}</span></span>
        </div>
        <div className="space-y-3">
          {["sep", "cog", "fun"].map((k) => {
            const s = scoring.subscales[k];
            return (
              <div key={k} data-testid={`grief-subscale-${k}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{subscales?.[k]?.label || s.label}</span>
                  <span className="text-muted-foreground">{s.mean} / 5</span>
                </div>
                <div className="h-2.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${s.percent}%`, background: COLORS[k] }} /></div>
                {subscales?.[k]?.hint && <p className="text-[11px] text-muted-foreground mt-0.5">{subscales[k].hint}</p>}
              </div>
            );
          })}
        </div>
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
          <p className="text-sm font-semibold" data-testid="grief-level"><span className={LEVEL_CLS[scoring.level] || ""}>{t("griefScale.level", "Niveau de chagrin")} : {t(`griefScale.level_${LEVEL_KEY[scoring.level] || "low"}`, scoring.level)}</span></p>
          <p className="text-sm text-muted-foreground">{levels?.[scoring.level] || scoring.level_text}</p>
          {scoring.prolonged_flag && <p className="text-sm text-rose-700 bg-rose-50 dark:bg-rose-950/30 rounded p-2" data-testid="grief-prolonged-flag">{t("griefScale.prolongedFlag", "Plus de six mois après la perte avec un chagrin intense qui retentit sur la vie : parlez-en avec votre thérapeute.")}</p>}
        </div>
        <p className="text-[11px] text-muted-foreground">{t("griefScale.disclaimer", "Outil de suivi non normé, inspiré des critères du deuil prolongé (CIM-11, DSM-5-TR) et des échelles PG-13 / ICG. Il ne constitue pas un diagnostic.")}</p>
      </CardContent>
    </Card>
  );
};

export const GriefTrendChart = ({ entries, subscales }) => {
  const { t, locale } = useI18n();
  const data = entries.map((e) => ({
    date: new Date(e.created_at).toLocaleDateString(locale === "en" ? "en-GB" : locale, { day: "numeric", month: "short" }),
    sep: e.scoring.subscales.sep.mean, cog: e.scoring.subscales.cog.mean, fun: e.scoring.subscales.fun.mean,
  }));
  return (
    <Card data-testid="grief-trend-chart">
      <CardHeader className="pb-2"><CardTitle className="text-base">{t("griefScale.trend", "Évolution dans le temps")}</CardTitle></CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
              <ReferenceLine y={3} stroke="#94a3b8" strokeDasharray="4 4" />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="sep" name={subscales?.sep?.label || "Séparation"} stroke={COLORS.sep} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="cog" name={subscales?.cog?.label || "Détresse / évitement"} stroke={COLORS.cog} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="fun" name={subscales?.fun?.label || "Retentissement"} stroke={COLORS.fun} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">{t("griefScale.trendHint", "Objectif : une baisse progressive des trois dimensions, en particulier du retentissement sur la vie.")}</p>
      </CardContent>
    </Card>
  );
};
